import * as Cesium from "cesium";

// Returns: [{ entity, id, negativePoints: [...], reasons: string }, ...]
export function getEntitiesWithNegativeHeights(dataSource) {
  if (!dataSource || !dataSource.entities) {
    return [];
  }

  const entities = dataSource.entities.values || dataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  const now = Cesium.JulianDate.now();

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) {
          if (typeof property.getValue === "function") {
            idFromProps = property.getValue();
          } else {
            idFromProps = property;
          }
        }
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) {
          if (typeof property.getValue === "function") {
            idFromProps = property.getValue();
          } else {
            idFromProps = property;
          }
        }
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) {
      return idFromProps;
    }

    return entity.id;
  }

  function getHeightFromPoint(p) {
    if (!p) return undefined;

    if (typeof Cesium !== "undefined" && Cesium.Cartesian3 && p instanceof Cesium.Cartesian3) {
      const carto = Cesium.Cartographic.fromCartesian(p);
      return carto && carto.height;
    }

    if (typeof p.x === "number" && typeof p.y === "number" && typeof p.z === "number") {
      if (typeof Cesium !== "undefined" && Cesium.Cartographic) {
        const carto = Cesium.Cartographic.fromCartesian(p);
        return carto && carto.height;
      }
      return p.z;
    }

    if (typeof p.height === "number") {
      return p.height;
    }

    if (typeof p.z === "number") {
      return p.z;
    }

    return undefined;
  }

  function toLonLatHeight(point, fallbackHeight) {
    if (!point) return null;

    if (typeof Cesium !== "undefined" && Cesium.Cartesian3 && (point instanceof Cesium.Cartesian3 || (typeof point.x === "number" && typeof point.y === "number" && typeof point.z === "number"))) {
      const cart = point instanceof Cesium.Cartesian3 ? point : new Cesium.Cartesian3(point.x, point.y, point.z);

      const carto = Cesium.Cartographic.fromCartesian(cart);
      if (!carto) return null;

      const lonDeg = Cesium.Math.toDegrees(carto.longitude);
      const latDeg = Cesium.Math.toDegrees(carto.latitude);
      const h = carto.height;

      return { lon: lonDeg, lat: latDeg, height: h };
    }

    if (typeof point.longitude === "number" && typeof point.latitude === "number") {
      let lon = point.longitude;
      let lat = point.latitude;

      if (Math.abs(lon) <= Math.PI * 2 && Math.abs(lat) <= Math.PI * 2) {
        lon = Cesium.Math.toDegrees(lon);
        lat = Cesium.Math.toDegrees(lat);
      }

      const h = typeof point.height === "number" ? point.height : fallbackHeight;
      return { lon, lat, height: h };
    }

    return null;
  }

  function collectNegativeFromValue(value, source) {
    const negatives = [];
    if (!value) return negatives;

    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "number") {
      for (let i = 0, idx = 0; i + 2 < value.length; i += 3, idx++) {
        const x = value[i];
        const y = value[i + 1];
        const z = value[i + 2];
        if (typeof z === "number" && z < 0) {
          negatives.push({
            point: { x, y, z },
            height: z,
            source,
            index: idx,
          });
        }
      }
      return negatives;
    }

    let points = [];

    if (Array.isArray(value)) {
      points = value;
    } else if (value && Array.isArray(value.positions)) {
      points = value.positions;
    }

    if (!Array.isArray(points) || points.length === 0) return negatives;

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const h = getHeightFromPoint(p);
      if (typeof h === "number" && h < 0) {
        negatives.push({
          point: p,
          height: h,
          source,
          index: i,
        });
      }
    }

    return negatives;
  }

  function collectNegativeFromCesiumProperty(prop, source) {
    if (!prop) return [];
    let value = prop;

    if (typeof prop.getValue === "function") {
      value = prop.getValue(now);
    }

    return collectNegativeFromValue(value, source);
  }

  function humanSourceLabel(source) {
    if (!source) return "geom";
    if (source.startsWith("polyline")) return "polyline";
    if (source.startsWith("polygon")) return "polygon";
    if (source.startsWith("corridor")) return "corridor";
    if (source.startsWith("wall")) return "wall";
    if (source === "position") return "position";
    return source;
  }

  function formatPointInfo(info) {
    const geo = toLonLatHeight(info.point, info.height);
    const h = info.height;
    const hStr = typeof h === "number" ? h.toFixed(2) : String(h);
    const label = humanSourceLabel(info.source);

    if (geo) {
      return label + "[" + info.index + "]: lon " + geo.lon.toFixed(6) + "°, lat " + geo.lat.toFixed(6) + "°, h " + hStr + " m";
    }

    return label + "[" + info.index + "]: h " + hStr + " m";
  }

  const result = [];

  for (const entity of entities) {
    if (!entity) continue;

    const negativePoints = [];

    if (entity.polyline && entity.polyline.positions) {
      negativePoints.push(...collectNegativeFromCesiumProperty(entity.polyline.positions, "polyline.positions"));
    }

    if (entity.polygon && entity.polygon.hierarchy) {
      const hierarchy = entity.polygon.hierarchy;
      let value = hierarchy;

      if (typeof hierarchy.getValue === "function") {
        value = hierarchy.getValue(Cesium.JulianDate.now());
      }

      if (value) {
        if (Array.isArray(value.positions)) {
          negativePoints.push(...collectNegativeFromValue(value.positions, "polygon.hierarchy.positions"));
        } else if (Array.isArray(value)) {
          negativePoints.push(...collectNegativeFromValue(value, "polygon.hierarchy"));
        }

        if (Array.isArray(value.holes)) {
          for (let hi = 0; hi < value.holes.length; hi++) {
            const hole = value.holes[hi];
            if (hole && Array.isArray(hole.positions)) {
              negativePoints.push(...collectNegativeFromValue(hole.positions, "polygon.hierarchy.hole[" + hi + "]"));
            }
          }
        }
      }
    }

    if (entity.corridor && entity.corridor.positions) {
      negativePoints.push(...collectNegativeFromCesiumProperty(entity.corridor.positions, "corridor.positions"));
    }

    if (entity.wall && entity.wall.positions) {
      negativePoints.push(...collectNegativeFromCesiumProperty(entity.wall.positions, "wall.positions"));
    }

    if (entity.position) {
      let pos = entity.position;
      if (typeof entity.position.getValue === "function") {
        pos = entity.position.getValue(Cesium.JulianDate.now());
      }
      const h = getHeightFromPoint(pos);
      if (typeof h === "number" && h < 0) {
        negativePoints.push({
          point: pos,
          height: h,
          source: "position",
          index: 0,
        });
      }
    }

    if (negativePoints.length > 0) {
      const id = getEntityId(entity);

      const maxShown = 5;
      const shown = negativePoints.slice(0, maxShown).map(formatPointInfo);
      let reasons = negativePoints.length + " negative point(s): " + shown.join(" | ");
      if (negativePoints.length > maxShown) {
        reasons += " | +" + (negativePoints.length - maxShown) + " more";
      }

      result.push({
        entity,
        id,
        negativePoints,
        reasons,
      });
    }
  }

  return result;
}
